import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  Edit, 
  Save, 
  X,
  FileText,
  Image as ImageIcon,
  Home,
  Phone,
  Users,
  Star,
  FolderOpen,
  Shield,
  MessageSquare
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import AdminMediaLibrary from './AdminMediaLibrary';
import { toRelativeMediaPath } from '../utils/mediaUrl';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminCMS = () => {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [activeSection, setActiveSection] = useState('all');
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const { user, token } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setContent(response.data);
    } catch (error) {
      console.error('Error fetching content:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke laste innhold",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setEditValue(item.value);
  };

  const handleSave = async () => {
    if (!editingItem) return;

    try {
      await axios.put(
        `${API}/content/${editingItem.key}`,
        { value: editValue },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast({
        title: "Lagret! ✅",
        description: "Innholdet er oppdatert",
      });

      fetchContent();
      setEditingItem(null);
      setEditValue('');
    } catch (error) {
      console.error('Error updating content:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke lagre endringer",
        variant: "destructive"
      });
    }
  };

  const handleMediaSelect = (image) => {
    // Use the medium version URL if available, otherwise original
    const rawUrl = image.versions?.medium?.url || image.url;
    setEditValue(toRelativeMediaPath(rawUrl));
    setShowMediaPicker(false);
    toast({
      title: "Bilde valgt",
      description: `Valgte: ${image.title || image.original_filename}`,
    });
  };

  const sections = [
    { id: 'all', label: 'Alle', icon: FileText },
    { id: 'hero', label: 'Hero', icon: Home },
    { id: 'contact', label: 'Kontakt', icon: Phone },
    { id: 'team', label: 'Team', icon: Users },
    { id: 'services', label: 'Tjenester', icon: Star },
    { id: 'trustbar', label: 'Sertifiseringer', icon: Shield },
    { id: 'footer', label: 'Footer', icon: MessageSquare }
  ];

  const filteredContent = activeSection === 'all' 
    ? content 
    : content.filter(item => item.section === activeSection);

  const getSectionIcon = (section) => {
    switch (section) {
      case 'hero': return <Home className="w-4 h-4" />;
      case 'contact': return <Phone className="w-4 h-4" />;
      case 'team': return <Users className="w-4 h-4" />;
      case 'services': return <Star className="w-4 h-4" />;
      case 'trustbar': return <Shield className="w-4 h-4" />;
      case 'footer': return <MessageSquare className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const getSectionColor = (section) => {
    switch (section) {
      case 'hero': return 'bg-blue-600/20 text-blue-400 border-blue-600/50';
      case 'contact': return 'bg-green-600/20 text-green-400 border-green-600/50';
      case 'team': return 'bg-purple-600/20 text-purple-400 border-purple-600/50';
      case 'services': return 'bg-yellow-600/20 text-yellow-400 border-yellow-600/50';
      case 'trustbar': return 'bg-orange-600/20 text-orange-400 border-orange-600/50';
      case 'footer': return 'bg-teal-600/20 text-teal-400 border-teal-600/50';
      default: return 'bg-gray-600/20 text-gray-400 border-gray-600/50';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white text-xl">Laster innhold...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center space-x-2">
            <FileText className="w-6 h-6 text-red-400" />
            <span>Innholdsredigering</span>
          </CardTitle>
          <CardDescription className="text-gray-400">
            Rediger tekster og innhold på nettsiden
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Section Filter */}
          <div className="flex flex-wrap gap-3 mb-6">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${
                    activeSection === section.id
                      ? 'bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg shadow-red-500/50 scale-105'
                      : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50 hover:text-white border border-gray-700'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{section.label}</span>
                </button>
              );
            })}
          </div>

          {/* Content List */}
          <div className="space-y-3">
            {filteredContent.length > 0 ? (
              filteredContent.map((item) => (
                <div
                  key={item.id}
                  className="bg-gray-900/50 border border-gray-700 rounded-xl p-4 hover:border-red-600/50 transition-all duration-300"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        {getSectionIcon(item.section)}
                        <h3 className="font-semibold text-white">{item.key}</h3>
                        <Badge className={getSectionColor(item.section)}>
                          {item.section}
                        </Badge>
                        <Badge className="bg-gray-700 text-gray-300">
                          {item.category}
                        </Badge>
                      </div>
                      
                      {item.description && (
                        <p className="text-sm text-gray-500 mb-2">{item.description}</p>
                      )}
                      
                      <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                        {item.category === 'toggle' ? (
                          <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                            item.value === 'true' ? 'bg-green-900/50 text-green-400' : 'bg-gray-700 text-gray-400'
                          }`}>
                            {item.value === 'true' ? 'Synlig' : 'Skjult'}
                          </span>
                        ) : (
                          <p className="text-gray-300 break-words">{item.value}</p>
                        )}
                      </div>
                    </div>

                    <Button
                      size="sm"
                      onClick={() => handleEdit(item)}
                      className="bg-blue-600 hover:bg-blue-700 text-white flex-shrink-0"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Rediger
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 text-gray-500">
                Ingen innhold funnet for denne seksjonen
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}>
        <DialogContent className="max-w-2xl bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              <span className="bg-gradient-to-r from-red-500 to-yellow-400 bg-clip-text text-transparent">
                Rediger Innhold
              </span>
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {editingItem?.key} - {editingItem?.section}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {editingItem?.description && (
              <div className="bg-blue-600/10 border border-blue-600/30 rounded-lg p-3">
                <p className="text-sm text-blue-400">{editingItem.description}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="content" className="text-white">Innhold</Label>
              {editingItem?.category === 'toggle' ? (
                <div className="flex items-center gap-4 p-4 bg-gray-800 rounded-lg border border-gray-700">
                  <button
                    type="button"
                    onClick={() => setEditValue(editValue === 'true' ? 'false' : 'true')}
                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors duration-300 ${
                      editValue === 'true' ? 'bg-green-600' : 'bg-gray-600'
                    }`}
                    data-testid="cms-toggle-switch"
                  >
                    <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform duration-300 ${
                      editValue === 'true' ? 'translate-x-7' : 'translate-x-1'
                    }`} />
                  </button>
                  <span className={`text-sm font-medium ${editValue === 'true' ? 'text-green-400' : 'text-gray-400'}`}>
                    {editValue === 'true' ? 'Synlig' : 'Skjult'}
                  </span>
                </div>
              ) : editingItem?.category === 'text' ? (
                <textarea
                  id="content"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  rows={5}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
                />
              ) : editingItem?.category === 'image' ? (
                <div className="space-y-3">
                  {/* Image Preview */}
                  {editValue && (
                    <div className="relative w-full h-40 bg-gray-800 rounded-lg overflow-hidden border border-gray-700">
                      <img
                        src={editValue.startsWith('/') ? editValue : editValue}
                        alt="Forhåndsvisning"
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  
                  {/* URL Input with Media Picker Button */}
                  <div className="flex gap-2">
                    <Input
                      id="content"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      placeholder="Bilde-URL eller velg fra mediebibliotek"
                      className="flex-1 bg-gray-800 border-gray-700 text-white focus:border-red-600"
                    />
                    <Button
                      type="button"
                      onClick={() => setShowMediaPicker(true)}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                      data-testid="open-media-picker-btn"
                    >
                      <FolderOpen className="w-4 h-4 mr-2" />
                      Velg bilde
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Lim inn en URL direkte eller velg et bilde fra mediebiblioteket
                  </p>
                </div>
              ) : (
                <Input
                  id="content"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white focus:border-red-600"
                />
              )}
            </div>

            <div className="flex space-x-3">
              <Button
                onClick={handleSave}
                className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold"
              >
                <Save className="w-4 h-4 mr-2" />
                Lagre Endringer
              </Button>
              <Button
                onClick={() => setEditingItem(null)}
                variant="outline"
                className="border-gray-600 text-gray-400 hover:bg-gray-800"
              >
                <X className="w-4 h-4 mr-2" />
                Avbryt
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Media Picker Dialog */}
      <Dialog open={showMediaPicker} onOpenChange={setShowMediaPicker}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-purple-400" />
              <span>Velg bilde fra mediebibliotek</span>
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Klikk på et bilde for å velge det
            </DialogDescription>
          </DialogHeader>
          
          <AdminMediaLibrary 
            pickerMode={true} 
            onSelect={handleMediaSelect}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCMS;
